import { IExtra, IProduct, ICartProduct, IStore, ICart } from "../classes/interfaces";
import { round2 } from "../classes/numbers";

/**
 * Sum of amount * price for every extra with amount > 0, each term rounded to 2
 * decimals before summing. The sum itself is NOT re-rounded — Store's original
 * calcTotalLocalProduct never re-rounded this accumulated value either, and adding
 * an outer round2() here changes rounding at floating-point summation boundaries
 * (e.g. amount=0.1, extras totalling exactly x.xx5 after summation) in roughly
 * 0.4-0.5% of realistic multi-extra carts, producing a total that differs by
 * R$0.01 from calcTotalLocalProduct's original output. Keep this unrounded to
 * stay byte-identical to the original.
 */
export function calcExtrasTotal(extras: IExtra[] | undefined): number {
  if (!extras || extras.length === 0) {
    return 0;
  }

  let total = 0;
  extras.forEach((extra) => {
    if (extra.amount && extra.amount > 0) {
      total += round2(extra.amount * extra.price);
    }
  });

  return total;
}

/**
 * amount * (unit price + extras total), rounded to 2 decimals.
 * Unit price is promotionalPrice when set and > 0, otherwise price.
 */
export function calcProductTotal(product: IProduct): number {
  const unitPrice =
    product.promotionalPrice && product.promotionalPrice > 0
      ? product.promotionalPrice
      : product.price;
  const extrasTotal = calcExtrasTotal(product.extras);
  const amount = product.amount || 0;

  return round2(amount * (unitPrice + extrasTotal));
}

/**
 * Extracted 1:1 from the old ModalFracionados component (business logic only,
 * no JSX). Mutates `product.total`/`product.totalExtra` in place — callers
 * must force a re-render after calling this, same as the original did via
 * its own local setState.
 *
 * "average": sum of round2(flavour.amount * flavour.price) across flavours.
 * anything else ("bigger"): the single highest flavour unit price.
 * Per-flavour extras are divided by the flavour count before being added to
 * totalExtra — this matches the unit price already being divided the same
 * way for display in the extras list (see useFlavourExtras, Task 3).
 */
export function calcFractionedProductTotal(product: ICartProduct): void {
  const store: IStore = JSON.parse(sessionStorage.getItem("@menuchef/store") || "{}");
  let totalExtra = 0;

  product.total = 0;

  if (store !== undefined && store !== null) {
    product.flavours!.forEach((flavour: ICartProduct) => {
      if ((store?.fractionalPrice || "average") === "average") {
        product.total += round2((flavour.amount || 0) * flavour.price);
      } else if (product.total < flavour.price) {
        product.total = flavour.price;
      }
    });
  }

  if (product.extras) {
    product.extras.forEach((extra) => {
      if (extra.amount && extra.amount > 0) {
        totalExtra += round2(extra.amount * extra.price);
      }
    });
  }

  if (product.flavours) {
    product.flavours.forEach((flavour) => {
      flavour.extras.forEach((extra) => {
        if (extra.amount && extra.amount > 0) {
          totalExtra += round2((extra.amount * extra.price) / product.flavours!.length);
        }
      });
    });
  }

  product.totalExtra = totalExtra;
  product.total += product.totalExtra;
  product.total = round2(product.total);
}

/**
 * Mutates cart in place — callers must setCart({...cart}) (or equivalent)
 * afterwards to re-render/persist.
 */
export function removeCartLine(cart: ICart, product: ICartProduct): void {
  cart.products = cart.products.filter((p) => p.id !== product.id);
  cart.total = round2(cart.products.reduce((sum, p) => sum + p.total, 0));
}

/**
 * Mutates cart/product in place — callers must setCart({...cart}) (or
 * equivalent) afterwards to re-render/persist. `newAmount <= 0` removes the
 * line entirely (via removeCartLine), which is why callers guard for
 * newAmount < 1 before calling this to decrement.
 */
export function setCartLineAmount(cart: ICart, product: ICartProduct, newAmount: number): void {
  if (newAmount <= 0) {
    removeCartLine(cart, product);
    return;
  }

  // Recompute totalExtra from extras rather than trusting the persisted value:
  // lines built by the steps flow (Store/confirmSteps) push step extras into
  // product.extras and fold their cost into total via totalSteps, but never
  // write them into product.totalExtra. Recomputing here keeps requantify from
  // dropping that cost, and also heals legacy carts persisted in sessionStorage.
  product.totalExtra = calcExtrasTotal(product.extras);
  product.amount = newAmount;
  product.totalProduct = round2(product.amount * product.price);
  product.total = product.totalProduct + round2(product.amount * product.totalExtra);
  cart.total = round2(cart.products.reduce((sum, p) => sum + p.total, 0));
}
